import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import myAxios from '../AxiosUrl';
import styles from './SEOManagement.module.css';
import {
    AiOutlineSearch,
    AiOutlinePlus,
    AiOutlineEdit,
    AiOutlineDelete,
    AiOutlineStar,
    AiOutlineCheck,
    AiOutlineClose
} from 'react-icons/ai';

function ReviewManagement() {
    const [reviews, setReviews] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingReview, setEditingReview] = useState(null);
    const [formData, setFormData] = useState({
        customerName: '',
        rating: 5,
        reviewText: '',
        productId: '',
        verified: false
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    const uid = localStorage.getItem('id');

    useEffect(() => {
        fetchReviews();
    }, [statusFilter]);

    const fetchReviews = async () => {
        setLoading(true);
        try {
            const response = await myAxios.get(`/seo/reviews/${uid}`, {
                params: {
                    status: statusFilter
                }
            });
            if (response.data.success) {
                setReviews(response.data.data);
                if (response.data.products) {
                    setProducts(response.data.products);
                }
            }
        } catch (error) {
            console.error('Error fetching reviews:', error);
            toast.error('Failed to fetch reviews');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (editingReview) {
                // Update existing review
                const response = await myAxios.put(
                    `/seo/review/${uid}/${editingReview.productId}/${editingReview._id}`,
                    formData
                );
                if (response.data.success) {
                    toast.success('Review updated successfully!');
                    fetchReviews();
                    handleCloseModal();
                }
            } else {
                // Create new review
                const response = await myAxios.post(`/seo/review/${uid}`, formData);
                if (response.data.success) {
                    toast.success('Review created successfully!');
                    fetchReviews();
                    handleCloseModal();
                }
            }
        } catch (error) {
            console.error('Error saving review:', error);
            toast.error('Failed to save review');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (review) => {
        if (!window.confirm('Are you sure you want to delete this review?')) return;

        setLoading(true);
        try {
            const response = await myAxios.delete(`/seo/review/${uid}/${review.productId}/${review._id}`);
            if (response.data.success) {
                toast.success('Review deleted successfully!');
                fetchReviews();
            }
        } catch (error) {
            console.error('Error deleting review:', error);
            toast.error('Failed to delete review');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (review) => {
        setLoading(true);
        try {
            const response = await myAxios.put(`/seo/review/${uid}/${review.productId}/${review._id}/approve`);
            if (response.data.success) {
                toast.success('Review approved successfully!');
                fetchReviews();
            }
        } catch (error) {
            console.error('Error approving review:', error);
            toast.error('Failed to approve review');
        } finally {
            setLoading(false);
        }
    };

    const handleDisapprove = async (review) => {
        setLoading(true);
        try {
            const response = await myAxios.put(`/seo/review/${uid}/${review.productId}/${review._id}/disapprove`);
            if (response.data.success) {
                toast.success('Review disapproved successfully!');
                fetchReviews();
            }
        } catch (error) {
            console.error('Error disapproving review:', error);
            toast.error('Failed to disapprove review');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (review) => {
        setEditingReview(review);
        setFormData({
            customerName: review.customerName,
            rating: review.rating,
            reviewText: review.reviewText,
            productId: review.productId || '',
            verified: review.verified !== undefined ? review.verified : false
        });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingReview(null);
        setFormData({
            customerName: '',
            rating: 5,
            reviewText: '',
            productId: '',
            verified: false
        });
    };

    const renderStars = (rating) => {
        const stars = [];
        for (let i = 1; i <= 5; i++) {
            stars.push(
                <AiOutlineStar
                    key={i}
                    style={{
                        color: i <= rating ? '#fbbf24' : '#d1d5db',
                        fontSize: '16px'
                    }}
                />
            );
        }
        return stars;
    };

    const getApprovalStatus = (review) => {
        if (review.approved === true) {
            return { label: 'Approved', color: '#10b981', bgColor: '#d1fae5' };
        } else if (review.approved === false) {
            return { label: 'Disapproved', color: '#ef4444', bgColor: '#fee2e2' };
        } else {
            return { label: 'Pending', color: '#f59e0b', bgColor: '#fef3c7' };
        }
    };

    const filteredReviews = reviews.filter(review =>
        review.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        review.reviewText?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        review.productName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className={styles.container}>
            <ToastContainer position="top-right" autoClose={3000} />

            <div className={styles.header}>
                <h1>Review Management</h1>
                <p>Manage customer reviews for SEO optimization and trust building</p>
            </div>

            <div className={styles.toolbar}>
                <div className={styles.searchBox}>
                    <AiOutlineSearch />
                    <input
                        type="text"
                        placeholder="Search reviews..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        style={{
                            padding: '10px 12px',
                            border: '1px solid #c9cccf',
                            borderRadius: '8px',
                            fontSize: '14px',
                            backgroundColor: 'white'
                        }}
                    >
                        <option value="all">All Reviews</option>
                        <option value="approved">Approved</option>
                        <option value="pending">Pending</option>
                        <option value="disapproved">Disapproved</option>
                    </select>
                    <button className={styles.addButton} onClick={() => setShowModal(true)}>
                        <AiOutlinePlus /> Add New Review
                    </button>
                </div>
            </div>

            {loading ? (
                <div className={styles.skeletonContainer}>
                    {/* Table Header Skeleton */}
                    <div style={{ display: 'flex', gap: '16px', padding: '14px 16px', background: '#f4f6f8', borderRadius: '8px 8px 0 0', marginBottom: '1px' }}>
                        {['100px', '90px', '200px', '120px', '70px', '90px', '110px'].map((w, i) => (
                            <div key={i} className={styles.skeleton} style={{ width: w, height: '13px' }}></div>
                        ))}
                    </div>

                    {/* Table Rows Skeleton */}
                    {[1, 2, 3, 4, 5, 6].map((item) => (
                        <div key={item} style={{ display: 'flex', gap: '16px', alignItems: 'center', padding: '16px', borderBottom: '1px solid #e1e3e5' }}>
                            {/* Customer Name */}
                            <div className={styles.skeleton} style={{ width: '100px', height: '14px' }}></div>
                            {/* Star Rating */}
                            <div style={{ display: 'flex', gap: '3px', width: '90px' }}>
                                {[1, 2, 3, 4, 5].map((s) => (
                                    <div key={s} className={styles.skeleton} style={{ width: '14px', height: '14px', borderRadius: '2px' }}></div>
                                ))}
                            </div>
                            {/* Review Text */}
                            <div className={styles.skeleton} style={{ width: '200px', height: '14px' }}></div>
                            {/* Product */}
                            <div className={styles.skeleton} style={{ width: '120px', height: '14px' }}></div>
                            {/* Verified Badge */}
                            <div className={styles.skeleton} style={{ width: '50px', height: '24px', borderRadius: '12px' }}></div>
                            {/* Status Badge */}
                            <div className={styles.skeleton} style={{ width: '70px', height: '24px', borderRadius: '12px' }}></div>
                            {/* Actions */}
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <div className={styles.skeleton} style={{ width: '32px', height: '32px', borderRadius: '6px' }}></div>
                                <div className={styles.skeleton} style={{ width: '32px', height: '32px', borderRadius: '6px' }}></div>
                                <div className={styles.skeleton} style={{ width: '32px', height: '32px', borderRadius: '6px' }}></div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Customer</th>
                            <th>Rating</th>
                            <th>Review</th>
                            <th>Product</th>
                            <th>Verified</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredReviews.length === 0 ? (
                            <tr>
                                <td colSpan="7" className={styles.emptyState}>
                                    No reviews found. Click "Add New Review" to create one.
                                </td>
                            </tr>
                        ) : (
                            filteredReviews.map((review) => {
                                const status = getApprovalStatus(review);
                                return (
                                    <tr key={review._id}>
                                        <td>{review.customerName}</td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '2px' }}>
                                                {renderStars(review.rating)}
                                            </div>
                                        </td>
                                        <td className={styles.answerCell}>
                                            {review.reviewText?.substring(0, 80)}...
                                        </td>
                                        <td>{review.productName || 'N/A'}</td>
                                        <td>
                                            <span className={review.verified ? styles.statusActive : styles.statusInactive}>
                                                {review.verified ? 'Yes' : 'No'}
                                            </span>
                                        </td>
                                        <td>
                                            <span style={{
                                                padding: '4px 12px',
                                                borderRadius: '12px',
                                                fontSize: '12px',
                                                fontWeight: '500',
                                                backgroundColor: status.bgColor,
                                                color: status.color
                                            }}>
                                                {status.label}
                                            </span>
                                        </td>
                                        <td>
                                            <div className={styles.actions}>
                                                {review.approved !== true && (
                                                    <button
                                                        className={styles.approveBtn}
                                                        onClick={() => handleApprove(review)}
                                                        title="Approve"
                                                        style={{
                                                            backgroundColor: '#10b981',
                                                            color: 'white',
                                                            border: 'none',
                                                            padding: '6px 10px',
                                                            borderRadius: '6px',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px'
                                                        }}
                                                    >
                                                        <AiOutlineCheck />
                                                    </button>
                                                )}
                                                {review.approved !== false && review.approved !== undefined && (
                                                    <button
                                                        className={styles.disapproveBtn}
                                                        onClick={() => handleDisapprove(review)}
                                                        title="Disapprove"
                                                        style={{
                                                            backgroundColor: '#ef4444',
                                                            color: 'white',
                                                            border: 'none',
                                                            padding: '6px 10px',
                                                            borderRadius: '6px',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px'
                                                        }}
                                                    >
                                                        <AiOutlineClose />
                                                    </button>
                                                )}
                                                <button
                                                    className={styles.editBtn}
                                                    onClick={() => handleEdit(review)}
                                                    title="Edit"
                                                >
                                                    <AiOutlineEdit />
                                                </button>
                                                <button
                                                    className={styles.deleteBtn}
                                                    onClick={() => handleDelete(review)}
                                                    title="Delete"
                                                >
                                                    <AiOutlineDelete />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
            )}

            {showModal && (
                <div className={styles.modalOverlay} onClick={handleCloseModal}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2>{editingReview ? 'Edit Review' : 'Add New Review'}</h2>
                            <button className={styles.closeBtn} onClick={handleCloseModal}>&times;</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className={styles.formGroup}>
                                <label>Product *</label>
                                <select
                                    value={formData.productId}
                                    onChange={(e) => setFormData({ ...formData, productId: e.target.value })}
                                    required
                                >
                                    <option value="">-- Select Product --</option>
                                    {products.map((product) => (
                                        <option key={product._id} value={product._id}>
                                            {product.productName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className={styles.formGroup}>
                                <label>Customer Name *</label>
                                <input
                                    type="text"
                                    value={formData.customerName}
                                    onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                                    required
                                    maxLength={100}
                                    placeholder="Enter customer name..."
                                />
                                <small>{formData.customerName.length}/100 characters</small>
                            </div>

                            <div className={styles.formGroup}>
                                <label>Rating *</label>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <button
                                            key={star}
                                            type="button"
                                            onClick={() => setFormData({ ...formData, rating: star })}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: '24px',
                                                color: star <= formData.rating ? '#fbbf24' : '#d1d5db',
                                                padding: '4px',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            <AiOutlineStar />
                                        </button>
                                    ))}
                                    <span style={{ marginLeft: '8px', fontSize: '14px', color: '#6d7175' }}>
                                        {formData.rating} star{formData.rating !== 1 ? 's' : ''}
                                    </span>
                                </div>
                            </div>

                            <div className={styles.formGroup}>
                                <label>Review Text *</label>
                                <textarea
                                    value={formData.reviewText}
                                    onChange={(e) => setFormData({ ...formData, reviewText: e.target.value })}
                                    required
                                    maxLength={500}
                                    rows={5}
                                    placeholder="Enter detailed review..."
                                />
                                <small>{formData.reviewText.length}/500 characters</small>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        checked={formData.verified}
                                        onChange={(e) => setFormData({ ...formData, verified: e.target.checked })}
                                    />
                                    Verified Purchase
                                </label>
                            </div>

                            <div className={styles.modalActions}>
                                <button type="button" className={styles.cancelBtn} onClick={handleCloseModal}>
                                    Cancel
                                </button>
                                <button type="submit" className={styles.saveBtn} disabled={loading}>
                                    {loading ? 'Saving...' : (editingReview ? 'Update Review' : 'Create Review')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ReviewManagement;
